<script setup>
import { computed, ref, watch } from 'vue'
import { useMeta } from '@/stores/meta.js'
import { getRecord } from '@/lib/records.js'
import { formatAddress } from '@/lib/format.js'
import { addressRoles } from '@/lib/addressRoles.js'

/**
 * An address taken from accounts' addresses (D-198): where an offer is deployed, picked among
 * the end customer's and the account's — a branch, a warehouse — instead of typed again.
 */
const props = defineProps({ accounts: { type: Array, default: () => [] }, disabled: Boolean })
const emit = defineEmits(['pick'])
const meta = useMeta()
const records = ref([])
watch(() => props.accounts.filter(Boolean).join(), async () => {
  const out = []
  for (const uuid of [...new Set(props.accounts.filter(Boolean))]) {
    try {
      out.push((await getRecord('account', uuid)).data)
    } catch {
      // not at hand: its addresses are not offered
    }
  }
  records.value = out
}, { immediate: true })
const roleLabel = (key) => addressRoles(meta).find((r) => r.key === key)?.label || key
const options = computed(() => records.value.flatMap((r) => (r.addresses || []).map((a) => ({
  key: a.uuid, address: a,
  label: `${r.label || r.name}: ${[a.label, (a.roles || []).map(roleLabel).join(', ')].filter(Boolean).join(' · ')}${a.label || (a.roles || []).length ? ' — ' : ''}${formatAddress(a)}`,
}))))
function pick(key) {
  const a = options.value.find((o) => o.key === key)?.address
  if (a) emit('pick', { street: a.street, building: a.building, postal_code: a.postal_code, city: a.city, region: a.region, country: a.country })
}
</script>

<template>
  <select v-if="options.length && !disabled" class="input mb-2 py-1 text-sm" data-testid="pick-account-address" :aria-label="$t('addresses.pickFromAccount')" @change="pick($event.target.value); $event.target.value = ''">
    <option value="">{{ $t('addresses.pickFromAccount') }}</option>
    <option v-for="o in options" :key="o.key" :value="o.key">{{ o.label }}</option>
  </select>
</template>
